<script setup>
import { computed, ref, watch } from 'vue'
import { api, asList } from '../api/client'
import { formatCurrency } from '../api/commerce'
import AppIcon from '../components/AppIcon.vue'
import CatalogDialog from '../components/CatalogDialog.vue'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import EmptyState from '../components/EmptyState.vue'
import LoadingState from '../components/LoadingState.vue'
import StockDialog from '../components/StockDialog.vue'
import { useToast } from '../composables/useToast'

const props = defineProps({ kind: { type: String, required: true } })
const { show } = useToast()
const items = ref([])
const loading = ref(true)
const loadError = ref('')
const search = ref('')
const stateFilter = ref('active')
const dialogOpen = ref(false)
const editingItem = ref(null)
const saving = ref(false)
const saveError = ref('')
const archiveItem = ref(null)
const changingState = ref(false)
const stockItem = ref(null)
const movements = ref([])
const movementsLoading = ref(false)
const stockSaving = ref(false)
const stockError = ref('')
let loadSequence = 0
let movementSequence = 0
const product = computed(() => props.kind === 'product')
const title = computed(() => product.value ? 'Товары' : 'Услуги')
const visibleItems = computed(() => items.value.filter((item) => {
  const term = search.value.trim().toLocaleLowerCase('ru')
  const matchesSearch = !term || `${item.name} ${item.sku || ''}`.toLocaleLowerCase('ru').includes(term)
  const matchesState = stateFilter.value === 'all' || (stateFilter.value === 'active' ? item.is_active : !item.is_active)
  return matchesSearch && matchesState
}).sort((a, b) => a.name.localeCompare(b.name, 'ru')))

async function load() {
  const sequence = ++loadSequence
  loading.value = true
  loadError.value = ''
  try {
    const rows = asList(await api.catalog.list({ kind: props.kind }))
    if (sequence === loadSequence) items.value = rows
  } catch (error) {
    if (sequence === loadSequence) loadError.value = error.message || 'Не удалось загрузить справочник.'
  } finally {
    if (sequence === loadSequence) loading.value = false
  }
}

watch(() => props.kind, () => {
  search.value = ''; stateFilter.value = 'active'; dialogOpen.value = false; stockItem.value = null; archiveItem.value = null
  ++movementSequence
  load()
}, { immediate: true })

function openEdit(item = null) {
  editingItem.value = item
  saveError.value = ''
  dialogOpen.value = true
}

async function saveItem(payload) {
  if (saving.value) return
  saving.value = true
  saveError.value = ''
  try {
    if (editingItem.value) await api.catalog.update(editingItem.value.id, payload)
    else await api.catalog.create(payload)
    dialogOpen.value = false
    show(editingItem.value ? 'Позиция обновлена. Старые продажи не изменились.' : 'Позиция добавлена.')
    await load()
  } catch (error) { saveError.value = error.message || 'Не удалось сохранить позицию.' }
  finally { saving.value = false }
}

async function toggleActive(item) {
  if (changingState.value) return
  changingState.value = true
  try {
    await api.catalog.update(item.id, { is_active: !item.is_active })
    show(item.is_active ? 'Позиция перенесена в архив. История сохранена.' : 'Позиция снова доступна для продаж.')
    archiveItem.value = null
    await load()
  } catch (error) { show(error.message || 'Не удалось изменить активность.', 'error') }
  finally { changingState.value = false }
}

async function loadMovements() {
  if (!stockItem.value) return
  const sequence = ++movementSequence
  const id = stockItem.value.id
  movementsLoading.value = true
  stockError.value = ''
  try {
    const rows = asList(await api.catalog.movements(id))
    if (sequence === movementSequence) movements.value = rows
  } catch (error) {
    if (sequence === movementSequence) stockError.value = error.message || 'Не удалось загрузить историю склада.'
  } finally {
    if (sequence === movementSequence) movementsLoading.value = false
  }
}

function openStock(item) {
  stockItem.value = item
  movements.value = []
  loadMovements()
}

async function saveMovement(payload) {
  if (!stockItem.value || stockSaving.value) return
  const id = stockItem.value.id
  stockSaving.value = true
  stockError.value = ''
  try {
    const updated = await api.catalog.move(id, payload)
    stockItem.value = updated
    items.value = items.value.map((item) => item.id === id ? updated : item)
    await load()
    stockItem.value = items.value.find((item) => item.id === id) || stockItem.value
    await loadMovements()
    show('Движение проведено, остаток обновлён.')
  } catch (error) { stockError.value = error.message || 'Не удалось провести движение.' }
  finally { stockSaving.value = false }
}
</script>

<template>
  <div class="page catalog-page">
    <header class="page-header"><div><span class="eyebrow">{{ product ? 'Справочник и склад' : 'Дополнительно к отдыху' }}</span><h1>{{ title }}</h1><p class="page-subtitle">{{ product ? 'Товары, остатки и история каждого прихода и списания.' : 'Услуги с ценами для продаж и бронирований.' }} Позиции архивируются, история не удаляется.</p></div>
      <button type="button" class="button button--primary" @click="openEdit()"><AppIcon name="plus" :size="18" />{{ product ? 'Добавить товар' : 'Добавить услугу' }}</button>
    </header>
    <section class="filter-bar" :aria-label="`Фильтры: ${title}`">
      <label class="search-field"><AppIcon name="search" :size="18" /><span class="sr-only">Поиск в справочнике</span><input v-model="search" type="search" placeholder="Название или артикул" /></label>
      <label class="compact-field"><span class="sr-only">Активность</span><select v-model="stateFilter"><option value="active">Активные</option><option value="inactive">В архиве</option><option value="all">Все позиции</option></select></label>
      <button type="button" class="button button--secondary" :disabled="loading" @click="load">Обновить</button>
    </section>
    <LoadingState v-if="loading" />
    <section v-else-if="loadError" class="data-section"><p class="form-error" role="alert">{{ loadError }}</p><button type="button" class="button button--secondary" @click="load">Повторить</button></section>
    <section v-else-if="visibleItems.length" class="data-section">
      <div class="table-scroll"><table class="data-table"><caption class="sr-only">{{ title }}</caption><thead><tr><th>Название</th><th>Цена</th><th v-if="product">На складе</th><th>Статус</th><th><span class="sr-only">Действия</span></th></tr></thead>
        <tbody><tr v-for="item in visibleItems" :key="item.id" :class="{ 'row--muted': !item.is_active }">
          <td data-label="Название"><strong>{{ item.name }}</strong><span class="cell-secondary">{{ item.sku ? `Артикул ${item.sku} · ` : '' }}{{ item.unit }}</span></td>
          <td data-label="Цена">{{ formatCurrency(item.unit_price) }}</td>
          <td v-if="product" data-label="На складе"><strong>{{ item.stock_quantity }} {{ item.unit }}</strong></td>
          <td data-label="Статус"><span class="availability" :class="item.is_active ? 'availability--free' : 'availability--inactive'">{{ item.is_active ? 'Активна' : 'В архиве' }}</span></td>
          <td class="row-actions"><button type="button" class="button button--small button--secondary" :aria-label="`Редактировать ${item.name}`" @click="openEdit(item)">Редактировать</button><button v-if="product" type="button" class="button button--small button--secondary" :aria-label="`Склад: ${item.name}`" @click="openStock(item)">Склад</button><button type="button" class="button button--small button--ghost" :disabled="changingState" @click="item.is_active ? archiveItem = item : toggleActive(item)">{{ item.is_active ? 'В архив' : 'Активировать' }}</button></td>
        </tr></tbody></table></div>
    </section>
    <EmptyState v-else title="Позиции не найдены" text="Добавьте товар или услугу либо измените фильтры." />
    <CatalogDialog :open="dialogOpen" :item="editingItem" :kind="kind" :saving="saving" :error="saveError" @close="!saving && (dialogOpen = false)" @save="saveItem" />
    <StockDialog :open="Boolean(stockItem)" :item="stockItem" :movements="movements" :loading="movementsLoading" :saving="stockSaving" :error="stockError" @close="!stockSaving && (stockItem = null, ++movementSequence)" @save="saveMovement" @reload="loadMovements" />
    <ConfirmDialog :open="Boolean(archiveItem)" title="Перенести позицию в архив?" :text="`«${archiveItem?.name || ''}» больше нельзя будет добавить в новую продажу. Старые продажи и складские движения сохранятся.`" confirm-label="В архив" :pending="changingState" @close="archiveItem = null" @confirm="toggleActive(archiveItem)" />
  </div>
</template>
